import { useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ShoppingCart, Loader2, Wrench, Building2, Package, ExternalLink, Undo2, Truck, Plus, Search } from "lucide-react";
import { useBuilderTrucks } from "@/components/search/useBuilderTrucks";
import { NoPhotoBypassPanel } from "@/components/NoPhotoBypass";

// a not-yet-finished part (a child just made, a part off a truck) goes on a job too — move now, finish later
const FIELD_ADD = ["staging_needs_id", "pending_review", "on_donor"];

function truckLabel(d) {
  const ymm = [d.year, d.make, d.model].filter(Boolean).join(" ");
  return ymm || d.stock_number || "Builder truck";
}

// Model C: the user picks a JOB (open build/PO, builder truck, customer order,
// internal use, or a brand-new job); the server resolves/creates the ONE open
// cart for it. No "which cart?" friction.
export function AddToCartButton({ part, compact = false, big = false, onAdded }) {
  const { has } = useAuth();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [openJobs, setOpenJobs] = useState([]);   // open draft carts (all types)
  const [query, setQuery] = useState("");
  const [busy, setBusy] = useState(false);
  const [warn, setWarn] = useState(null);          // {carts:[...]} soft on-other-cart warning
  const [noPhoto, setNoPhoto] = useState(null);    // no tag/photo yet: alert, then the logged bypass
  const [lastPayload, setLastPayload] = useState(null);
  // new-job form
  const [showNew, setShowNew] = useState(false);
  const [atype, setAtype] = useState("build");
  const [buildName, setBuildName] = useState("");
  const [po, setPo] = useState("");
  const [custName, setCustName] = useState("");
  const [walkIn, setWalkIn] = useState(false);
  const [notes, setNotes] = useState("");
  const [openBuildPOs, setOpenBuildPOs] = useState(() => new Set());
  // the builder trucks come from the ONE truck engine (S4), not a filter over a list loaded in the browser
  // (its box also finds jobs by customer or PO, so its words are not recorded as a truck search)
  const builderRes = useBuilderTrucks(query, open && !showNew && has("build_cart"), { withPo: true });

  const canBuild = has("build_cart");
  const canRelease = has("approve_cart");
  const reservedCartId = part.reserved_cart_id;
  const isAvailable = !reservedCartId && (part.status === "available" || FIELD_ADD.includes(part.status));

  // ---- reserved: show which cart holds it + View + Release ----
  if (reservedCartId) {
    const label = part.reserved_label || part.reserved_cart_number || "Reserved";
    const rpo = part.reserved_po;
    const release = async () => {
      setBusy(true);
      try {
        await api.post("/carts/release-part", { part_id: part.id });
        toast.success(`${part.short_code} released back to stock`);
        onAdded?.();
      } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
      finally { setBusy(false); }
    };
    return (
      <div className="inline-flex items-center gap-1.5 flex-wrap" data-testid={`in-cart-${part.short_code}`}>
        <span className="inline-flex items-center gap-1 rounded-lg bg-amber-50 text-amber-800 border border-amber-200 px-2 py-1 text-[11px] font-medium">
          <ShoppingCart className="w-3.5 h-3.5" />{rpo ? <>On PO {rpo}</> : (label && label !== part.reserved_cart_number ? label : "On a build/order")}
          <span className="font-mono text-amber-500/80"> · {part.reserved_cart_number}</span>
        </span>
        <Link to={`/pull-carts?open=${reservedCartId}`} data-testid={`view-cart-${part.short_code}`}
          className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-2 py-1 text-[11px] text-slate-600 hover:border-slate-400">
          <ExternalLink className="w-3 h-3" />View
        </Link>
        {canRelease && (
          <Button data-testid={`release-part-${part.short_code}`} size="sm" variant="outline" disabled={busy}
            className="rounded-lg h-7 border-[#B01E28] text-[#B01E28] hover:bg-red-50 text-[11px]" onClick={release}>
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Undo2 className="w-3.5 h-3.5 mr-1" />Release</>}
          </Button>
        )}
      </div>
    );
  }

  if (!isAvailable) return null;
  if (!canBuild) return null;

  const resetForm = () => {
    setShowNew(false); setAtype("build"); setBuildName(""); setPo("");
    setCustName(""); setWalkIn(false); setNotes(""); setQuery(""); setWarn(null); setNoPhoto(null); setLastPayload(null);
  };

  const openDialog = async () => {
    setOpen(true); resetForm(); setLoading(true);
    try {
      const cartsRes = await api.get("/carts", { params: { status: "draft" } });
      const jobs = (cartsRes.data || []).filter((c) => !c.sample_purged);
      setOpenJobs(jobs);
      // builder trucks that DON'T already have an open build cart (those are listed as jobs)
      setOpenBuildPOs(new Set(
        jobs.filter((c) => c.assignment_type === "build").map((c) => (c.build?.po_number || "").trim()).filter(Boolean)));
    } catch { setOpenJobs([]); }
    finally { setLoading(false); }
  };

  // Single source of truth: send {job payload} to /carts/add-to-job.
  const addToJob = async (payload, force = false) => {
    setBusy(true);
    setLastPayload(payload);
    try {
      const { data } = await api.post("/carts/add-to-job", { part_id: part.id, force, ...payload });
      const rpo = data.build?.po_number;
      const dest = rpo ? `PO ${rpo}`
        : data.build?.name
        || (data.assignment_type === "customer_order" ? (data.customer_order?.customer_name || "the order")
          : data.assignment_type === "internal_use" ? "internal use"
          : data.cart_number);
      toast.success(`Added ${part.short_code} to ${dest}${payload.floor_override ? " — no photo yet, reason logged" : ""}`);
      setOpen(false); resetForm();
      onAdded?.(data);
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d && typeof d === "object" && d.code === "on_other_cart") setWarn({ carts: d.carts || [] });
      else if (d && typeof d === "object" && d.code === "field_add_blocked" && d.override_available) setNoPhoto(d);
      else if (d && typeof d === "object" && d.code === "already_on_this_job") toast.warning(d.message);
      else toast.error(formatApiErrorDetail(d));
    } finally { setBusy(false); }
  };

  const addWithReason = ({ reason, note }) => addToJob({ ...lastPayload, floor_override: true,
    floor_override_reason: reason, floor_override_note: note });

  const addAnyway = async () => {
    setWarn(null);
    if (lastPayload) await addToJob(lastPayload, true);
  };

  const submitNew = () => {
    if (atype === "build") return addToJob({ assignment_type: "build", build: { name: buildName, po_number: po } });
    if (atype === "customer_order") return addToJob({ assignment_type: "customer_order", customer_order: { customer_name: custName, walk_in: walkIn, order_ref: walkIn ? undefined : notes, notes: walkIn ? notes : undefined } });
    return addToJob({ assignment_type: "internal_use", internal_use: { purpose: notes, notes } });
  };

  // filter open jobs + builders by the search box
  const q = query.trim().toLowerCase();
  const jobsShown = openJobs.filter((c) => {
    if (!q) return true;
    const b = c.build || {}; const co = c.customer_order || {};
    return [c.cart_number, b.name, b.po_number, co.customer_name, co.order_ref, co.po_number, c.assignment_type]
      .filter(Boolean).some((x) => String(x).toLowerCase().includes(q));
  });
  const buildersShown = builderRes.items.filter((d) => (d.po_number || "").trim() && !openBuildPOs.has((d.po_number || "").trim()));

  const jobIcon = (t) => t === "build" ? Wrench : t === "customer_order" ? Building2 : Package;
  const jobTitle = (c) => {
    if (c.assignment_type === "build") return c.build?.name || "Build";
    if (c.assignment_type === "customer_order") return c.customer_order?.customer_name || "Customer Order";
    return "Internal Use";
  };
  const jobSub = (c) => {
    if (c.assignment_type === "build") return c.build?.po_number ? `PO ${c.build.po_number}` : "no PO";
    if (c.assignment_type === "customer_order") { const co = c.customer_order || {}; return co.walk_in ? "Walk-in" : `Ref ${co.order_ref || co.po_number || "—"}`; }
    return c.internal_use?.purpose || "Internal";
  };

  const RowBtn = ({ testid, Icon, title, sub, badge, onClick }) => (
    <button data-testid={testid} onClick={onClick} disabled={busy}
      className="w-full flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-3 py-3 text-left hover:border-[#E0781A] hover:bg-orange-50/40 transition-colors disabled:opacity-50">
      <span className="shrink-0 w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center text-slate-600">
        <Icon className="w-5 h-5" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-semibold text-slate-800 truncate">{title}</span>
        <span className="block text-xs text-slate-500 truncate">{sub}</span>
      </span>
      {badge != null && <span className="shrink-0 text-[11px] text-slate-400">{badge}</span>}
    </button>
  );

  return (
    <>
      <Button data-testid={`add-to-cart-${part.short_code}`} size="sm" variant="outline"
        className={`rounded-lg border-slate-200 hover:border-[#E0781A] hover:text-[#E0781A] ${big ? "h-12 px-4 text-sm font-semibold" : "h-7 text-xs"}`}
        title="Add to a build or order" onClick={openDialog}>
        <ShoppingCart className={`${big ? "w-4 h-4" : "w-3.5 h-3.5"} mr-1`} />{compact ? "Add" : "Add to Build/Order"}
      </Button>
      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) resetForm(); }}>
        <DialogContent data-testid="add-to-job-dialog" className="rounded-lg max-w-lg">
          <DialogHeader><DialogTitle>Add {part.short_code} to a build/order</DialogTitle></DialogHeader>

          {noPhoto ? (
            <NoPhotoBypassPanel block={noPhoto} busy={busy} actionLabel="Add to the job — logged"
              after="The photo is still needed before the job is signed off."
              onConfirm={addWithReason} onCancel={() => setNoPhoto(null)} />
          ) : warn ? (
            <div data-testid="add-to-job-warn" className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900">
              <div className="font-semibold flex items-center gap-1 mb-1"><ShoppingCart className="w-3.5 h-3.5" />Already on another cart</div>
              {warn.carts.map((c) => (
                <div key={c.id} className="flex items-center gap-2">
                  <span>{c.display || c.cart_number}</span>
                  <Link to={`/pull-carts?open=${c.id}`} className="underline inline-flex items-center gap-0.5"><ExternalLink className="w-3 h-3" />View</Link>
                </div>
              ))}
              <div className="mt-1 text-amber-700">Add it to this job anyway? (Only one cart can reserve it at approval.)</div>
            </div>
          ) : showNew ? (
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-1">
                {[["build", "Build", Wrench], ["customer_order", "Customer", Building2], ["internal_use", "Internal", Package]].map(([v, l, Icon]) => (
                  <button key={v} data-testid={`new-job-atype-${v}`} onClick={() => setAtype(v)}
                    className={`text-[11px] py-2 rounded-lg border flex flex-col items-center gap-1 ${atype === v ? "border-[#B01E28] bg-white text-[#B01E28]" : "border-slate-200 text-slate-500"}`}>
                    <Icon className="w-4 h-4" />{l}
                  </button>
                ))}
              </div>
              {atype === "build" && (
                <>
                  <Input data-testid="new-job-build-po" placeholder="PO number (required)" value={po} onChange={(e) => setPo(e.target.value)} className="rounded-lg h-10 text-sm font-mono" autoFocus />
                  <Input data-testid="new-job-build-name" placeholder="Build name (optional)" value={buildName} onChange={(e) => setBuildName(e.target.value)} className="rounded-lg h-10 text-sm" />
                </>
              )}
              {atype === "customer_order" && (
                <>
                  <Input data-testid="new-job-cust-name" placeholder="Customer name (required)" value={custName} onChange={(e) => setCustName(e.target.value)} className="rounded-lg h-10 text-sm" />
                  <label className="flex items-center gap-2 text-xs text-slate-600">
                    <input data-testid="new-job-walkin" type="checkbox" checked={walkIn} onChange={(e) => setWalkIn(e.target.checked)} />
                    Walk-in / counter sale (no PO/ref)
                  </label>
                  <Input data-testid="new-job-notes" placeholder={walkIn ? "Notes (optional)" : "Order/invoice ref or PO"} value={notes} onChange={(e) => setNotes(e.target.value)} className="rounded-lg h-10 text-sm" />
                </>
              )}
              {atype === "internal_use" && (
                <Input data-testid="new-job-notes" placeholder="Purpose / reason (required)" value={notes} onChange={(e) => setNotes(e.target.value)} className="rounded-lg h-10 text-sm" />
              )}
            </div>
          ) : (
            <div className="space-y-3">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input data-testid="job-search-input" autoFocus placeholder="Search jobs — PO, build, truck, customer…"
                  value={query} onChange={(e) => setQuery(e.target.value)} className="rounded-lg h-10 text-sm pl-9" />
              </div>
              <div className="max-h-[46dvh] overflow-y-auto space-y-2 pr-0.5">
                {loading ? (
                  <div className="flex items-center justify-center py-8 text-slate-400"><Loader2 className="w-5 h-5 animate-spin" /></div>
                ) : (
                  <>
                    {jobsShown.length > 0 && (
                      <div className="space-y-1.5">
                        <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400 px-1">Open jobs</div>
                        {jobsShown.map((c) => (
                          <RowBtn key={c.id} testid={`job-option-${c.id}`} Icon={jobIcon(c.assignment_type)}
                            title={jobTitle(c)} sub={`${c.cart_number} · ${jobSub(c)}`} badge={`${c.line_count} parts`}
                            onClick={() => addToJob({ cart_id: c.id })} />
                        ))}
                      </div>
                    )}
                    {buildersShown.length > 0 && (
                      <div className="space-y-1.5 pt-1">
                        <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400 px-1">Builder trucks</div>
                        {buildersShown.map((d) => (
                          <RowBtn key={d.id} testid={`builder-option-${d.id}`} Icon={Truck}
                            title={truckLabel(d)} sub={`PO ${d.po_number} · ${d.stock_number}`}
                            onClick={() => addToJob({ assignment_type: "build", build: { name: truckLabel(d), builder_id: d.id } })} />
                        ))}
                        {builderRes.total > builderRes.items.length && (
                          <div data-testid="builder-options-more" className="px-1 text-[11px] font-semibold text-[#C76714]">
                            More builder trucks match than this list holds ({builderRes.total} in all) — type more to narrow it down.
                          </div>
                        )}
                      </div>
                    )}
                    {!loading && jobsShown.length === 0 && buildersShown.length === 0 && (
                      <div className="text-center text-sm text-slate-400 py-6">No open jobs match — start a new one below.</div>
                    )}
                  </>
                )}
              </div>
            </div>
          )}

          {!noPhoto && <DialogFooter>
            {warn ? (
              <>
                <Button variant="outline" className="rounded-lg border-slate-200" onClick={() => setWarn(null)}>Back</Button>
                <Button data-testid="add-anyway-confirm" onClick={addAnyway} disabled={busy} className="rounded-lg bg-amber-600 hover:bg-amber-700 text-white">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Add anyway"}
                </Button>
              </>
            ) : showNew ? (
              <>
                <Button variant="outline" className="rounded-lg border-slate-200" onClick={() => setShowNew(false)} disabled={busy}>Back</Button>
                <Button data-testid="add-to-job-confirm" onClick={submitNew} disabled={busy} className="rounded-lg bg-[#E0781A] hover:bg-[#C76714]">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create job & add"}
                </Button>
              </>
            ) : (
              <Button data-testid="new-job-toggle" onClick={() => { setShowNew(true); setWarn(null); }} className="rounded-lg bg-[#E0781A] hover:bg-[#C76714]">
                <Plus className="w-4 h-4 mr-1" />New build/order
              </Button>
            )}
          </DialogFooter>}
        </DialogContent>
      </Dialog>
    </>
  );
}
